/**
 * /records — the departmental archive: the yearly file plans (номенклатура
 * дел / ishlar nomenklaturasi) and their cases.
 *
 * Shown where the project's Records policy is on and the person holds
 * `records:read` (`records:manage` to change anything) — `GET
 * /embed/v1/records/access` says so. The retention catalogue the cases draw
 * on is kept in the project console (Retention tab).
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Folder, Library, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { LocationTabs } from '@/modules/my-docs/components/listing/LocationTabs.jsx';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { records, showsRecords, showsReview, useRecordsAccess } from '../lib/api.js';
import { LIMITS, planProblems } from '../lib/validation.js';
import { ReviewView } from '../components/ReviewView.jsx';
import { CasesView } from '../components/CasesView.jsx';
import { JournalsView } from '../components/JournalsView.jsx';
import { PlanView } from '../components/PlanView.jsx';
import { PLAN_STATUS_TONE as STATUS_TONE } from '../lib/retention.js';

const NO_COPY = 'none';

function NewPlanDialog({ open, onOpenChange, plans, onCreated }) {
  const { t } = useTranslation('records');
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [title, setTitle] = useState('');
  const [copyFrom, setCopyFrom] = useState(NO_COPY);
  const [busy, setBusy] = useState(false);
  const checks = useFieldErrors(planProblems({ year, title }, plans.map((p) => p.year)));
  const { reset } = checks;

  useEffect(() => {
    if (!open) return;
    reset();
    const taken = new Set(plans.map((p) => p.year));
    let next = thisYear;
    while (taken.has(next)) next += 1;
    setYear(next);
    setTitle(plans[0]?.title || '');
    setCopyFrom(plans.length ? String(plans[0].year) : NO_COPY);
  }, [open, plans, thisYear, reset]);

  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    const res = await records.createPlan({
      year: Number(year),
      title: title.trim() || null,
      copy_from_year: copyFrom === NO_COPY ? null : Number(copyFrom),
    });
    setBusy(false);
    if (res.success) {
      toast.success(t('plan.created', { year: res.response.year }));
      onCreated(res.response);
    } else {
      checks.showServer(res, ['year', 'title', 'copy_from_year']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('plan.newTitle')}</DialogTitle>
          <DialogDescription>{t('plan.newDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="plan-year" className={REQUIRED_LABEL}>{t('plan.year')}</Label>
            <Input id="plan-year" type="number" required min={LIMITS.yearMin} max={LIMITS.yearMax} step={1} value={year}
              onChange={(e) => { setYear(e.target.value); checks.touch('year'); }} {...checks.describe('year', 'plan')} />
            <FieldError id="plan-year-error" message={checks.errors.year} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-plan-title">{t('plan.organisation')}</Label>
            <Input id="new-plan-title" maxLength={LIMITS.planTitle} value={title}
              onChange={(e) => { setTitle(e.target.value); checks.touch('title'); }} {...checks.describe('title', 'plan')} />
            <FieldError id="plan-title-error" message={checks.errors.title} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('plan.copyFrom')}</Label>
            <Select value={copyFrom} onValueChange={setCopyFrom}>
              <SelectTrigger aria-label={t('plan.copyFrom')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_COPY}>{t('plan.startEmpty')}</SelectItem>
                {plans.map((p) => <SelectItem key={p.id} value={String(p.year)}>{t('plan.copyOf', { year: p.year })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !year || checks.blocked} data-testid="plan-create">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('plan.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RecordsPage() {
  const { t } = useTranslation(['records', 'mainPage']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const access = useRecordsAccess();
  const [plans, setPlans] = useState(null);
  const [classes, setClasses] = useState([]);
  const [creating, setCreating] = useState(false);
  const allowed = showsRecords(access);
  const views = ['plans', 'cases', 'journals', ...(showsReview(access) ? ['review'] : [])];
  const view = views.includes(searchParams.get('view')) ? searchParams.get('view') : 'plans';
  const selectedCase = Number(searchParams.get('case')) || null;
  const selected = Number(searchParams.get('plan')) || plans?.[0]?.id || null;
  const selectedJournal = Number(searchParams.get('journal')) || null;

  const loadPlans = useCallback(async () => {
    const res = await records.plans();
    if (res.success) setPlans(res.response);
    else toast.error(res.message);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadPlans();
    records.catalogue().then((res) => {
      if (res.success) setClasses(res.response.classes);
    });
  }, [allowed, loadPlans]);

  const select = (id) => setSearchParams(id ? { plan: String(id) } : {}, { replace: true });
  const selectJournal = (id) => setSearchParams(id ? { view: 'journals', journal: String(id) } : { view: 'journals' }, { replace: true });
  const switchView = (next) => setSearchParams(next === 'plans' ? {} : { view: next }, { replace: true });
  const selectCase = (id) => setSearchParams(id ? { view: 'cases', case: String(id) } : { view: 'cases' }, { replace: true });

  const tabs = [
    { value: 'my_documents', label: t('mainPage:locations.myDocuments'), icon: <Folder className="w-4 h-4" />, onClick: () => navigate('/my-docs') },
    { value: 'records', label: t('records:title'), icon: <Library className="w-4 h-4" />, onClick: () => {} },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-6xl px-3 py-4 md:px-6">
        <button
          type="button"
          onClick={() => navigate('/my-docs')}
          className="mb-3 -ml-2 inline-flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" /> {t('records:back')}
        </button>
        <h1 className="mb-1 text-lg font-semibold text-foreground">{t('records:title')}</h1>
        <p className="mb-3 text-xs text-muted-foreground">{t('records:subtitle')}</p>

        {!access ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : !allowed ? (
          <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="records-unavailable">
            {t(access.enabled ? 'records:noAccess' : 'records:off')}
          </p>
        ) : (
          <>
            <LocationTabs tabs={tabs} activeValue="records" className="mb-4 rounded-md bg-muted/60 p-1" />
            <div className="mb-4 flex gap-1" role="tablist">
              {views.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={view === value}
                  onClick={() => switchView(value)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium cursor-pointer',
                    view === value ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                  data-testid={`records-view-${value}`}
                >
                  {t(`records:views.${value}`)}
                </button>
              ))}
            </div>
            {view === 'journals' ? (
              <JournalsView access={access} selectedId={selectedJournal} onSelect={selectJournal} />
            ) : view === 'review' ? (
              <ReviewView access={access} />
            ) : view === 'cases' ? (
              <CasesView access={access} classes={classes} selectedCase={selectedCase} onSelectCase={selectCase} />
            ) : (
            <div className="grid gap-6 md:grid-cols-[14rem_1fr]">
              <aside className="space-y-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-foreground">{t('records:plans')}</h2>
                  {access.can_manage && (
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2" onClick={() => setCreating(true)} data-testid="plan-new">
                      <Plus className="h-3.5 w-3.5" /> {t('records:plan.new')}
                    </Button>
                  )}
                </div>
                {!plans ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : plans.length === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="plans-empty">{t(access.can_manage ? 'records:noPlansManage' : 'records:noPlans')}</p>
                ) : (
                  <ul className="space-y-1">
                    {plans.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => select(p.id)}
                          aria-current={p.id === selected ? 'page' : undefined}
                          className={cn(
                            'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted cursor-pointer',
                            p.id === selected && 'bg-muted font-medium',
                          )}
                        >
                          <span>
                            {p.year}
                            <span className="block text-xs font-normal text-muted-foreground">{t('records:plan.cases', { count: p.cases })}</span>
                          </span>
                          <Badge variant="outline" className={STATUS_TONE[p.status]}>{t(`records:plan.statuses.${p.status}`)}</Badge>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </aside>
              <section>
                {selected ? (
                  <PlanView
                    key={selected}
                    planId={selected}
                    canManage={access.can_manage}
                    canReview={access.can_review}
                    classes={classes}
                    onChanged={loadPlans}
                    onDeleted={() => {
                      select(null);
                      loadPlans();
                    }}
                  />
                ) : (
                  plans && <p className="py-16 text-center text-sm text-muted-foreground">{t('records:pickPlan')}</p>
                )}
              </section>
            </div>
            )}
          </>
        )}
      </div>
      <NewPlanDialog
        open={creating}
        onOpenChange={setCreating}
        plans={plans || []}
        onCreated={(plan) => {
          setCreating(false);
          loadPlans();
          select(plan.id);
        }}
      />
    </div>
  );
}
